/** The room-check probes (screens C1). Each is a plain function over an injected browser surface, so a test can fail any one of them. */
import { CheckStreamEvent, ROUTES } from "@bsim/contracts";
import { SSE_UNHEALTHY_AFTER_MS } from "../api/sse-health";

export type CheckId = "viewport" | "cookies" | "stream" | "worker" | "fonts";
export type CheckStatus = "pending" | "pass" | "warn" | "fail";
export interface CheckResult { readonly status: Exclude<CheckStatus, "pending">; readonly detail: string }

/** Phones and tablets are supported (screens 1.3, 8): any width from the narrowest phone passes; a laptop shows more at once. */
export const MIN_VIEWPORT_PX = 360;
export const LAPTOP_VIEWPORT_PX = 1152;
const PINGS = 3;
/** Three pings arrive about 1 s apart; slower than this passes with a warning (a proxy that buffers). */
const STREAM_PASS_MS = 5_000;
/** ADR-0022 preview budget. */
export const WORKER_BUDGET_MS = 1_500;
const FONT_FAMILIES = ["Inter Variable", "Playfair Display Variable", "Source Serif 4 Variable"] as const;
const FONT_WAIT_MS = 5_000;

export function checkViewport(width: number): CheckResult {
  if (width < MIN_VIEWPORT_PX) return { status: "warn", detail: `${width} px wide; the game needs at least ${MIN_VIEWPORT_PX} px, so widen the window or turn the device sideways` };
  return width >= LAPTOP_VIEWPORT_PX
    ? { status: "pass", detail: `${width} px wide` }
    : { status: "pass", detail: `${width} px wide; works on this device, a laptop shows more at once` };
}

export function checkCookies(doc: { cookie: string }, secure: boolean): CheckResult {
  const name = "bsim_check";
  try {
    doc.cookie = `${name}=1; Path=/; Max-Age=60; SameSite=Lax${secure ? "; Secure" : ""}`;
    const stored = doc.cookie.split("; ").includes(`${name}=1`);
    doc.cookie = `${name}=; Path=/; Max-Age=0`;
    return stored ? { status: "pass", detail: "A cookie can be set and read" } : { status: "fail", detail: "The browser refused a first-party cookie" };
  } catch {
    return { status: "fail", detail: "Cookies are blocked" };
  }
}

export interface EventSourceLike {
  addEventListener(type: string, listener: (e: MessageEvent<string>) => void): void;
  onerror: ((e: Event) => void) | null;
  close(): void;
}

/** Passes on three distinct `check.ping`s; an error, a wrong payload or the deadline is a fail, never an open spinner. */
export function checkStream(open: (url: string) => EventSourceLike, now: () => number, timeoutMs = SSE_UNHEALTHY_AFTER_MS): Promise<CheckResult> {
  return new Promise((resolve) => {
    const started = now();
    const seen = new Set<number>();
    let source: EventSourceLike | undefined;
    const finish = (result: CheckResult): void => { clearTimeout(timer); source?.close(); resolve(result); };
    const timer = setTimeout(() => finish({ status: "fail", detail: `Only ${seen.size} of ${PINGS} pings arrived in ${timeoutMs / 1000} s; the proxy may be buffering or blocking the stream` }), timeoutMs);
    try {
      source = open(ROUTES.checkStream.path);
    } catch {
      finish({ status: "fail", detail: "This browser could not open a live stream" });
      return;
    }
    source.onerror = () => finish({ status: "fail", detail: `The stream closed after ${seen.size} of ${PINGS} pings` });
    source.addEventListener("check.ping", (e) => {
      const ping = CheckStreamEvent.safeParse(safeJson(e.data));
      if (ping.success) seen.add(ping.data.n);
      if (seen.size < PINGS) return;
      const ms = now() - started;
      finish(ms <= STREAM_PASS_MS ? { status: "pass", detail: `${PINGS} pings in ${(ms / 1000).toFixed(1)} s` } : { status: "warn", detail: `${PINGS} pings took ${(ms / 1000).toFixed(1)} s; live updates are slow on this network` });
    });
  });
}

const safeJson = (text: string): unknown => { try { return JSON.parse(text) as unknown; } catch { return null; } };

export interface WorkerLike { onmessage: ((e: MessageEvent) => void) | null; onerror: ((e: ErrorEvent) => void) | null; postMessage(m: unknown): void; terminate(): void }

/** Runs the bundled sample in a Worker; startup included, it must finish inside the preview budget. */
export function checkWorker(spawn: () => WorkerLike, now: () => number): Promise<CheckResult> {
  return new Promise((resolve) => {
    const started = now();
    let worker: WorkerLike | undefined;
    const finish = (result: CheckResult): void => { clearTimeout(timer); worker?.terminate(); resolve(result); };
    const timer = setTimeout(() => finish({ status: "fail", detail: `The worker did not answer within ${WORKER_BUDGET_MS / 1000} s` }), WORKER_BUDGET_MS);
    try {
      worker = spawn();
    } catch {
      finish({ status: "fail", detail: "This browser blocked the forecast worker" });
      return;
    }
    worker.onerror = () => finish({ status: "fail", detail: "This browser blocked the forecast worker" });
    worker.onmessage = () => {
      const ms = Math.round(now() - started);
      finish({ status: "pass", detail: `Sample ran in ${ms} ms (budget ${WORKER_BUDGET_MS} ms)` });
    };
    worker.postMessage("run");
  });
}

export async function checkFonts(fonts: { load(font: string): Promise<unknown[]> }): Promise<CheckResult> {
  const loaded = Promise.all(FONT_FAMILIES.map((family) => fonts.load(`1em "${family}"`))).then((sets) => sets.every((set) => set.length > 0));
  const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), FONT_WAIT_MS));
  const ok = await Promise.race([loaded, timeout]).catch(() => false);
  return ok ? { status: "pass", detail: "Interface, display and reading fonts loaded" } : { status: "fail", detail: "The bundled fonts did not load; the page would fall back to system fonts" };
}
